import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Globe, Loader2, Building2, CreditCard, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { PlugZap } from 'lucide-react';
import { ALL_ADDON_DEFINITIONS, addonsForVertical, mergeAddonCatalog } from '../lib/aiDashboardAddons';
import { addonsToPersist } from '../lib/tenantAddons';
import { workspaceAdminAPI, verticalAPI, addonAPI } from '../services/api';
import { DomainSetup } from '../components/RebrandingModule';
import { attachCustomDomain, normaliseHost } from '../lib/tenantCustomDomain';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { CardSkeleton } from '../components/common/States';

const SectionCard = ({ icon: Icon, title, description, children }) => (
  <Card>
    <CardHeader>
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-[18px] w-[18px]" />
        </div>
        <div>
          <CardTitle className="text-hr-lg">{title}</CardTitle>
          {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">{children}</CardContent>
  </Card>
);

const normalizePlansPayload = (payload) => {
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
      ? payload.items
      : [];

  return rows
    .map((plan) => ({
      id: plan?.id,
      code: String(plan?.code || '').trim(),
      name: String(plan?.name || '').trim(),
      is_active: Boolean(plan?.is_active ?? true),
      monthly_price: Number(plan?.monthly_price || 0),
      yearly_price: Number(plan?.yearly_price || 0),
      pricing_controls: plan?.pricing_controls || {},
      usage_limits: plan?.usage_limits || {},
    }))
    .filter((plan) => Boolean(plan.id || plan.code || plan.name))
    .map((plan) => ({
      ...plan,
      code: plan.code || `plan_${plan.id ?? 'unknown'}`,
      name: plan.name || plan.code || `Plan ${plan.id ?? ''}`.trim(),
    }));
};

const formatPlanMoney = (value, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

// Build the per-tenant add-on state from the tenant's subscription override,
// falling back to the plan's add-ons and each definition's default pricing.
const buildAddonState = (override, planLimits, definitions = ALL_ADDON_DEFINITIONS) => {
  const overrideAddons = Array.isArray(override?.addons) ? override.addons : null;
  const overridePricing = override?.addon_pricing || {};
  const planAddons = Array.isArray(planLimits?.addons) ? planLimits.addons : [];
  const planPricing = planLimits?.addon_pricing || {};
  const state = {};
  definitions.forEach((def) => {
    // If the tenant has an explicit override list, respect it; otherwise inherit
    // the plan's enabled add-ons as the starting point.
    const enabled = overrideAddons ? overrideAddons.includes(def.key) : planAddons.includes(def.key);
    const pricing = overridePricing[def.key] || planPricing[def.key] || def.defaultPricing || {};
    state[def.key] = {
      enabled,
      monthly_price: String(Number(pricing.monthly_price ?? def.defaultPricing?.monthly_price ?? 0)),
      yearly_price: String(Number(pricing.yearly_price ?? def.defaultPricing?.yearly_price ?? 0)),
    };
  });
  return state;
};

const SuperadminTenantEditPage = ({ tenantId, onBack, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [addonState, setAddonState] = useState({});
  // The tenant's own vertical bundle. Decides which add-ons are worth offering:
  // an IT/SaaS workspace has no use for Faculty Management or Accreditation.
  const [verticalBundle, setVerticalBundle] = useState(null);
  // The vertical is what the tenant chose at onboarding, so it is shown rather
  // than offered. Changing it re-provisions modules, navigation, roles and
  // dashboards, which is not something to do by brushing past a dropdown.
  const [changingVertical, setChangingVertical] = useState(false);
  const planInitRef = useRef(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    status: 'active',
    vertical: 'generic',
    planId: '',
  });
  // Vertical options come from the registry (add a JSON file -> appears here). Static
  // fallback keeps the control usable if the endpoint is unavailable.
  // The registry is the list. Two hand-maintained copies of it lived here and
  // in the create page, and both drifted the moment a vertical JSON file was
  // added — the file appeared in the API and in neither dropdown.
  const [verticalOptions, setVerticalOptions] = useState([{ key: 'generic', label: 'General' }]);
  // The add-on catalog the server actually grants from. Falls back to the
  // compiled-in list, so a console that cannot reach the endpoint is no worse
  // off than it was before the endpoint existed.
  const [addonCatalog, setAddonCatalog] = useState(ALL_ADDON_DEFINITIONS);

  useEffect(() => {
    let active = true;
    addonAPI.registry()
      .then((res) => { if (active) setAddonCatalog(mergeAddonCatalog(res?.data?.addons)); })
      .catch(() => { /* compiled-in list stands */ });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    verticalAPI.listRegistry()
      .then((res) => {
        const list = res?.data?.verticals;
        if (active && Array.isArray(list) && list.length) setVerticalOptions(list);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!formData.vertical) return undefined;
    let active = true;
    verticalAPI.previewRegistry(formData.vertical)
      .then((res) => { if (active) setVerticalBundle(res?.data || null); })
      // Left null on failure, which `addonsForVertical` reads as "offer
      // everything". Showing too much is recoverable; hiding an add-on the
      // tenant pays for is what drops it on the next save.
      .catch(() => { if (active) setVerticalBundle(null); });
    return () => { active = false; };
  }, [formData.vertical]);

  const heldAddonKeys = useMemo(() => {
    const override = tenant?.subscription_usage_limits_override;
    return new Set(Array.isArray(override?.addons) ? override.addons : []);
  }, [tenant]);

  const scopedAddons = useMemo(
    () => addonsForVertical(verticalBundle, addonCatalog), [verticalBundle, addonCatalog]);
  const outsideAddons = useMemo(() => {
    const scoped = new Set(scopedAddons.map((def) => def.key));
    return addonCatalog.filter((def) => !scoped.has(def.key) && heldAddonKeys.has(def.key));
  }, [scopedAddons, heldAddonKeys, addonCatalog]);
  const renderedAddons = useMemo(() => [...scopedAddons, ...outsideAddons], [scopedAddons, outsideAddons]);
  const renderedAddonKeys = useMemo(() => renderedAddons.map((def) => def.key), [renderedAddons]);

  const verticalLabel = useMemo(
    () => verticalOptions.find((opt) => opt.key === formData.vertical)?.label
      || verticalBundle?.label
      || formData.vertical
      || '—',
    [verticalOptions, formData.vertical, verticalBundle]
  );

  const selectedPlan = useMemo(
    () => subscriptionPlans.find((plan) => String(plan.id) === String(formData.planId)),
    [subscriptionPlans, formData.planId]
  );
  const planSelectValue = formData.planId ? String(formData.planId) : '__unassigned__';
  const selectedPlanCurrency = selectedPlan?.pricing_controls?.currency || 'INR';

  // Two groups, both rendered. The vertical's own add-ons, and anything this
  // tenant already holds that the vertical would not have offered — usually
  // inherited from a plan. The second group is shown rather than hidden
  // because an add-on the editor does not render is one it cannot switch off,
  // and hiding a paid entitlement is how it gets lost.
  // The catalog arrives after the tenant does, so any add-on only the server
  // knows about has no state yet. Backfilled rather than rebuilt: a rebuild
  // would discard whatever was toggled while the request was in flight.
  useEffect(() => {
    const override = tenant?.subscription_usage_limits_override;
    const overrideAddons = Array.isArray(override?.addons) ? override.addons : null;
    const planAddons = Array.isArray(selectedPlan?.usage_limits?.addons)
      ? selectedPlan.usage_limits.addons : [];
    setAddonState((prev) => {
      const missing = addonCatalog.filter((def) => !(def.key in prev));
      if (missing.length === 0) return prev;
      const next = { ...prev };
      missing.forEach((def) => {
        // Same rule buildAddonState uses: an explicit override list is the
        // answer, otherwise the plan's add-ons are inherited. Reading only the
        // override would show a plan-granted add-on as switched off.
        next[def.key] = {
          enabled: overrideAddons ? overrideAddons.includes(def.key) : planAddons.includes(def.key),
          monthly_price: String(Number(def.defaultPricing?.monthly_price ?? 0)),
          yearly_price: String(Number(def.defaultPricing?.yearly_price ?? 0)),
        };
      });
      return next;
    });
  }, [addonCatalog, tenant, selectedPlan]);

  const setAddon = (key, patch) =>
    setAddonState((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  // Summed over the add-ons actually rendered. State is built for the whole
  // catalog, so summing all of it would bill an education module to an IT/SaaS
  // tenant that is never shown one and cannot turn it off.
  const addonMonthlyTotal = useMemo(
    () => renderedAddonKeys.reduce(
      (sum, key) => sum + (addonState[key]?.enabled ? Number(addonState[key].monthly_price || 0) : 0), 0),
    [addonState, renderedAddonKeys]
  );
  const addonYearlyTotal = useMemo(
    () => renderedAddonKeys.reduce(
      (sum, key) => sum + (addonState[key]?.enabled ? Number(addonState[key].yearly_price || 0) : 0), 0),
    [addonState, renderedAddonKeys]
  );
  const selectedPlanCurrentMonthlyTotal = Number(selectedPlan?.monthly_price || 0) + addonMonthlyTotal;
  const selectedPlanCurrentYearlyTotal = Number(selectedPlan?.yearly_price || 0) + addonYearlyTotal;

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        let [tenantsResponse, plansResponse] = await Promise.all([
          workspaceAdminAPI.listTenants(),
          workspaceAdminAPI.listSubscriptionPlans({ include_inactive: true }),
        ]);
        const tenants = Array.isArray(tenantsResponse.data) ? tenantsResponse.data : [];
        let plans = normalizePlansPayload(plansResponse?.data);

        if (plans.length === 0) {
          await workspaceAdminAPI.bootstrapDefaultPlanAndSeed();
          plansResponse = await workspaceAdminAPI.listSubscriptionPlans({ include_inactive: true });
          plans = normalizePlansPayload(plansResponse?.data);
        }

        const currentTenant = tenants.find((row) => Number(row.id) === Number(tenantId));
        if (!mounted) return;
        if (!currentTenant) {
          toast.error('Tenant not found');
          onBack?.();
          return;
        }

        const currentPlanCode = String(currentTenant.subscription_plan_code || '').trim();
        const currentPlanName = String(currentTenant.subscription_plan_name || '').trim();
        const planIdFromCode = plans.find((plan) => String(plan.code) === currentPlanCode)?.id;
        const resolvedPlanId = currentTenant.subscription_plan_id || planIdFromCode || '';
        const hasResolvedPlan = plans.some((plan) => String(plan.id) === String(resolvedPlanId));
        const normalizedPlans = hasResolvedPlan || !resolvedPlanId
          ? plans
          : [
              ...plans,
              {
                id: resolvedPlanId,
                code: currentPlanCode || `plan_${resolvedPlanId}`,
                name: currentPlanName || `Plan ${resolvedPlanId}`,
                is_active: true,
              },
            ];

        const resolvedPlan = normalizedPlans.find((plan) => String(plan.id) === String(resolvedPlanId));
        setSubscriptionPlans(normalizedPlans);
        setTenant(currentTenant);
        setAddonState(buildAddonState(
          currentTenant.subscription_usage_limits_override, resolvedPlan?.usage_limits));
        planInitRef.current = false;
        setFormData({
          name: currentTenant.name || '',
          slug: currentTenant.slug || '',
          status: currentTenant.status || 'active',
          vertical: currentTenant.vertical || 'generic',
          planId: String(resolvedPlanId),
        });
      } catch (error) {
        if (!mounted) return;
        toast.error(getErrorMessage(error, 'Failed to load tenant details'));
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [tenantId, onBack]);

  // When the superadmin switches the plan (after initial load), inherit the new
  // plan's add-ons as fresh defaults so per-tenant pricing starts from that plan.
  useEffect(() => {
    if (!selectedPlan) return;
    if (!planInitRef.current) { planInitRef.current = true; return; }
    setAddonState(buildAddonState(null, selectedPlan.usage_limits, addonCatalog));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan?.id]);

  const handleSave = async () => {
    if (!tenant) return;
    if (!formData.name.trim() || !formData.slug.trim()) {
      toast.error('Tenant name and slug are required');
      return;
    }

    try {
      setSaving(true);
      await workspaceAdminAPI.updateTenant(tenant.id, {
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        status: formData.status,
        vertical: formData.vertical,
      });

      if (formData.planId) {
        // Persist per-tenant add-on selection + superadmin-set pricing into the
        // subscription usage-limits override (preserving other override fields).
        const enabledKeys = renderedAddonKeys.filter((key) => addonState[key]?.enabled);
        const existingOverride = (tenant?.subscription_usage_limits_override && typeof tenant.subscription_usage_limits_override === 'object')
          ? tenant.subscription_usage_limits_override
          : {};
        // `addons` REPLACES the plan's list rather than merging into it, so
        // whatever goes here is the tenant's whole entitlement. Only the keys
        // this page rendered a toggle for are decided by it — `white_label`,
        // `extra_users` and the rest have no toggle and are carried through.
        // Rebuilding the list from the rendered catalog alone is what dropped
        // `white_label` from a live tenant and stopped its custom domain
        // resolving.
        const addons = addonsToPersist({
          existingAddons: existingOverride.addons,
          managedKeys: renderedAddonKeys,
          enabledKeys,
        });
        const addonPricing = { ...(existingOverride.addon_pricing || {}) };
        renderedAddonKeys.forEach((key) => {
          const st = addonState[key];
          if (st?.enabled) {
            addonPricing[key] = {
              monthly_price: Number(st.monthly_price) || 0,
              yearly_price: Number(st.yearly_price) || 0,
              currency: selectedPlanCurrency,
            };
          } else {
            delete addonPricing[key];
          }
        });
        await workspaceAdminAPI.assignTenantSubscription(tenant.id, {
          plan_id: Number(formData.planId),
          usage_limits_override: { ...existingOverride, addons, addon_pricing: addonPricing },
        });
      }

      toast.success('Tenant updated successfully');
      onSaved?.();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update tenant'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <CardSkeleton rows={3} />
        <CardSkeleton rows={5} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Tenants
        </Button>
      </div>

      <SectionCard
        icon={Building2}
        title="Identity"
        description="Core organization details for this tenant."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="tenant-name">Organization Name</Label>
            <Input
              id="tenant-name"
              value={formData.name}
              onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tenant-slug">Slug</Label>
            <Input
              id="tenant-slug"
              value={formData.slug}
              onChange={(event) => setFormData((prev) => ({ ...prev, slug: event.target.value }))}
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard
        icon={CreditCard}
        title="Plan &amp; Access"
        description="Subscription plan, status, and billing summary."
      >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tenant-status">Status</Label>
              <select
                id="tenant-status"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={formData.status}
                onChange={(event) => setFormData((prev) => ({ ...prev, status: event.target.value }))}
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tenant-vertical">Vertical</Label>
              {changingVertical ? (
                <>
                  <select
                    id="tenant-vertical"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.vertical}
                    onChange={(event) => setFormData((prev) => ({ ...prev, vertical: event.target.value }))}
                  >
                    {verticalOptions.map((opt) => (
                      <option key={opt.key} value={opt.key}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-amber-600 dark:text-amber-500">
                    Saving a different vertical re-provisions this workspace&apos;s modules, navigation,
                    roles and dashboards, and changes which add-ons it is offered.
                  </p>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    id="tenant-vertical"
                    readOnly
                    tabIndex={-1}
                    className="bg-muted/40 text-muted-foreground"
                    value={verticalLabel}
                  />
                  <Button type="button" variant="outline" size="sm" onClick={() => setChangingVertical(true)}>
                    Change
                  </Button>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Chosen at onboarding. It decides which features below are on offer.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tenant-plan">Plan</Label>
              <Select
                value={planSelectValue}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    planId: value === '__unassigned__' ? '' : value,
                  }))
                }
              >
                <SelectTrigger id="tenant-plan" className="w-full">
                  <SelectValue placeholder="Select Plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassigned__">Select Plan</SelectItem>
                  {subscriptionPlans.length === 0 ? (
                    <SelectItem value="__no_plans_available" disabled>
                      No subscription plans available
                    </SelectItem>
                  ) : (
                    subscriptionPlans.map((plan) => (
                      <SelectItem key={plan.id} value={String(plan.id)}>
                        {plan.name || plan.code}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
            <div>
              Current Plan: <span className="font-medium text-foreground">{selectedPlan?.name || tenant?.subscription_plan_name || 'Unassigned'}</span>
            </div>
            {selectedPlan ? (
              <>
                <div className="mt-2">
                  Base Price: <span className="font-medium text-foreground">{formatPlanMoney(selectedPlan.monthly_price, selectedPlanCurrency)}/month · {formatPlanMoney(selectedPlan.yearly_price, selectedPlanCurrency)}/year</span>
                </div>
                <div className="mt-1">
                  Add-on Uplift: <span className="font-medium text-foreground">{formatPlanMoney(addonMonthlyTotal, selectedPlanCurrency)}/month · {formatPlanMoney(addonYearlyTotal, selectedPlanCurrency)}/year</span>
                </div>
                <div className="mt-1">
                  Subscription Total: <span className="font-semibold text-foreground">{formatPlanMoney(selectedPlanCurrentMonthlyTotal, selectedPlanCurrency)}/month · {formatPlanMoney(selectedPlanCurrentYearlyTotal, selectedPlanCurrency)}/year</span>
                </div>
              </>
            ) : null}
          </div>
      </SectionCard>

      <SectionCard
        icon={PlugZap}
        title="Add-ons"
        description="Features from this tenant's vertical, at the monthly / yearly price you set. Every one switched on is added to the subscription total below. Overrides the plan defaults for this tenant only."
      >
        {!selectedPlan ? (
          <p className="text-sm text-muted-foreground">Assign a subscription plan above to manage add-ons.</p>
        ) : (
          <div className="space-y-3">
            {scopedAddons.map((def) => {
              const st = addonState[def.key] || { enabled: false, monthly_price: '0', yearly_price: '0' };
              return (
                <div key={def.key} className={`rounded-xl border p-4 transition-colors ${st.enabled ? 'border-primary/40 bg-primary/[0.03]' : 'border-border bg-card'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-primary" />
                        <p className="font-medium text-foreground">{def.label}</p>
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{def.description}</p>
                    </div>
                    <Switch checked={!!st.enabled} onCheckedChange={(v) => setAddon(def.key, { enabled: Boolean(v) })} />
                  </div>
                  {st.enabled ? (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Monthly Price ({selectedPlanCurrency})</Label>
                        <Input type="number" min="0" value={st.monthly_price}
                          onChange={(e) => setAddon(def.key, { monthly_price: e.target.value })} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Yearly Price ({selectedPlanCurrency})</Label>
                        <Input type="number" min="0" value={st.yearly_price}
                          onChange={(e) => setAddon(def.key, { yearly_price: e.target.value })} />
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}

            {outsideAddons.length > 0 ? (
              <div className="space-y-3 pt-2">
                <div className="rounded-lg border border-amber-300/60 bg-amber-50/60 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
                  <span className="font-medium">Held outside this vertical.</span>{' '}
                  {verticalLabel} does not offer these, but this tenant holds them — usually
                  inherited from its plan. Shown so they can be switched off; they are billed
                  while they stay on.
                </div>
                {outsideAddons.map((def) => {
                  const st = addonState[def.key] || { enabled: false, monthly_price: '0', yearly_price: '0' };
                  return (
                <div key={def.key} className={`rounded-xl border p-4 transition-colors ${st.enabled ? 'border-primary/40 bg-primary/[0.03]' : 'border-border bg-card'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-primary" />
                        <p className="font-medium text-foreground">{def.label}</p>
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{def.description}</p>
                    </div>
                    <Switch checked={!!st.enabled} onCheckedChange={(v) => setAddon(def.key, { enabled: Boolean(v) })} />
                  </div>
                  {st.enabled ? (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Monthly Price ({selectedPlanCurrency})</Label>
                        <Input type="number" min="0" value={st.monthly_price}
                          onChange={(e) => setAddon(def.key, { monthly_price: e.target.value })} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Yearly Price ({selectedPlanCurrency})</Label>
                        <Input type="number" min="0" value={st.yearly_price}
                          onChange={(e) => setAddon(def.key, { yearly_price: e.target.value })} />
                      </div>
                    </div>
                  ) : null}
                </div>
                  );
                })}
              </div>
            ) : null}

            <div className="flex items-center justify-between rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm">
              <span className="text-muted-foreground">Add-on total for this tenant</span>
              <span className="font-semibold text-foreground">{formatPlanMoney(addonMonthlyTotal, selectedPlanCurrency)}/month · {formatPlanMoney(addonYearlyTotal, selectedPlanCurrency)}/year</span>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </SectionCard>

      <CustomDomainSection tenantId={tenantId} tenantName={tenant?.name} />
    </div>
  );
};

/**
 * A tenant's custom domain, for a tenant that already exists.
 *
 * It could only be set while *creating* one. After that the console had no
 * field for it at all and the operator was told, in an error string, to go and
 * find the workspace's own Rebranding screen — which means signing in as them.
 * The endpoint has been able to do this the whole time; nothing called it.
 *
 * Writes in the same order the creation flow does: authorise the domain, then
 * save it. The reverse order stores a domain the tenant is not allowed to serve.
 */
const CustomDomainSection = ({ tenantId, tenantName }) => {
  const [domain, setDomain] = useState('');
  const [saved, setSaved] = useState('');
  const [records, setRecords] = useState([]);
  const [verifiedAt, setVerifiedAt] = useState(null);
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    let dead = false;
    workspaceAdminAPI.getTenantBranding(tenantId)
      .then(({ data }) => {
        if (dead) return;
        const current = data?.branding?.custom_domain || '';
        setDomain(current);
        setSaved(current);
      })
      .catch(() => { /* a tenant with no branding row yet simply has none */ });
    return () => { dead = true; };
  }, [tenantId]);

  const save = async () => {
    const wanted = normaliseHost(domain);
    setBusy(true);
    try {
      if (!wanted) {
        // Clearing it is a normal thing to want, and it needs no authorisation
        // step — there is no domain left to authorise.
        const { data } = await workspaceAdminAPI.updateTenantBranding(tenantId, { custom_domain: null });
        setSaved(''); setDomain(''); setRecords([]); setVerifiedAt(null); setCheck(null);
        toast.success('Custom domain removed');
        return data;
      }
      const outcome = await attachCustomDomain(workspaceAdminAPI, tenantId, wanted);
      if (!outcome.ok) {
        toast.error(`Could not save the domain while ${outcome.failedStep}: ${getErrorMessage(outcome.error, 'no reason given')}`);
        return null;
      }
      setSaved(wanted);
      setRecords(outcome.records || []);
      setVerifiedAt(outcome.verifiedAt);
      toast.success('Domain saved — it serves once the DNS records below resolve');
      return outcome;
    } finally {
      setBusy(false);
    }
  };

  // Runs against this tenant, not the operator's own workspace — that is what
  // the tenant_id makes the endpoint do.
  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await workspaceAdminAPI.verifyTenantCustomDomain(tenantId);
      setCheck(data);
      if (data?.status === 'live') { setVerifiedAt(new Date().toISOString()); toast.success(data.summary); }
      else toast.message(data?.summary || 'Not ready yet');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not check the domain'));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <SectionCard
      icon={Globe}
      title="Custom domain"
      description={`Where ${tenantName || 'this workspace'} answers. Needs the white-label add-on to actually serve.`}
    >
      <div className="space-y-2">
        <Label htmlFor="tenant-custom-domain">Domain</Label>
        <div className="flex gap-2">
          <Input
            id="tenant-custom-domain"
            value={domain}
            placeholder="hr.theircompany.com"
            onChange={(e) => setDomain(e.target.value)}
          />
          <Button onClick={save} disabled={busy || normaliseHost(domain) === saved}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : saved && !normaliseHost(domain) ? 'Remove' : 'Save'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          A domain the tenant owns. Their workspace already answers on its portal subdomain with
          nothing to set up — this is only for a domain of their own.
        </p>
      </div>

      {saved && records.length > 0 ? (
        <DomainSetup
          records={records}
          verifiedAt={verifiedAt}
          check={check}
          onVerify={verify}
          verifying={verifying}
        />
      ) : saved ? (
        <div className="rounded-lg border border-border bg-muted/30 p-3 flex flex-wrap items-center gap-2">
          <p className="text-xs text-muted-foreground flex-1 min-w-0">
            {verifiedAt ? `Verified ${new Date(verifiedAt).toLocaleDateString()}.` : 'Not verified yet.'}
            {' '}Save again to fetch the DNS records this domain needs.
          </p>
          <Button size="sm" variant="outline" onClick={verify} disabled={verifying}>
            {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check DNS'}
          </Button>
        </div>
      ) : null}
    </SectionCard>
  );
};

export default SuperadminTenantEditPage;
